import { useMemo, useState, type FormEvent } from 'react';
import type { TagRef } from '@neuroedge/shared';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import { useCreateTag, useTags } from '../../api/tags';
import { TagChips } from './TagChips';
import { validateTag } from './tag-validation';

interface Props {
  title?: string;
  /** Tags currently on the record. */
  tags: TagRef[] | undefined;
  /** Full-set replace with the chosen tag ids. */
  onSave: (tagIds: string[]) => Promise<unknown>;
}

/**
 * Card that shows a record's tags and, for users with `tags.manage`, lets them
 * toggle which workspace tags apply and create a new tag inline. Saving performs
 * a full-set replace via the parent's `onSave`.
 */
export function TagPicker({ title = 'Tags', tags, onSave }: Props) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('tags.manage');
  const all = useTags();
  const createTag = useCreateTag();
  const toast = useToast();

  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#3366ff');
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const current = useMemo(() => (tags ?? []).map((t) => t.id), [tags]);

  function begin() {
    setSelected(new Set(current));
    setFormError(null);
    setFieldErrors({});
    setEditing(true);
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const errors = validateTag(newName, newColor);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }
    setFormError(null);
    try {
      const created = await createTag.mutateAsync({ name: newName.trim(), color: newColor });
      setSelected((prev) => new Set(prev).add(created.id));
      setNewName('');
      setFieldErrors({});
      toast.success('Tag created.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setFormError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
          return;
        }
      }
      const message = err instanceof Error ? err.message : 'Could not create the tag.';
      setFormError(message);
      toast.error(message);
    }
  }

  async function save() {
    setSaving(true);
    setFormError(null);
    try {
      await onSave([...selected]);
      setEditing(false);
      toast.success('Tags updated.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save tags.';
      setFormError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title={title}>
      {!editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <TagChips
            tags={tags}
            empty={<span style={{ color: 'var(--color-text-muted)' }}>No tags.</span>}
          />
          <span style={{ flex: 1 }} />
          {canManage ? (
            <Button variant="secondary" onClick={begin}>
              Edit tags
            </Button>
          ) : null}
        </div>
      ) : all.isPending ? (
        <PageState kind="loading" message="Loading tags…" />
      ) : all.isError ? (
        <PageState kind="error" message={all.error.message} onRetry={() => void all.refetch()} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            {all.data.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
                No tags in this workspace yet. Create one below.
              </p>
            ) : (
              all.data.map((t) => (
                <label
                  key={t.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(t.id)}
                    onChange={() => toggle(t.id)}
                  />
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: t.color ?? '#64748b',
                      display: 'inline-block',
                    }}
                  />
                  {t.name}
                </label>
              ))
            )}
          </div>

          <form
            onSubmit={onCreate}
            style={{ display: 'flex', alignItems: 'end', gap: 'var(--space-2)', flexWrap: 'wrap' }}
          >
            <TextField
              label="New tag"
              placeholder="e.g. VIP"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setFieldErrors((f) => {
                  if (!f.name) return f;
                  const { name: _removed, ...rest } = f;
                  return rest;
                });
              }}
              maxLength={60}
              error={fieldErrors.name}
            />
            <div className="ne-field">
              <label className="ne-field__label" htmlFor="tp-new-color">
                Colour
              </label>
              <input
                id="tp-new-color"
                type="color"
                className="ne-input"
                style={{ width: 48, padding: 2 }}
                value={newColor}
                onChange={(e) => {
                  setNewColor(e.target.value);
                  setFieldErrors((f) => {
                    if (!f.color) return f;
                    const { color: _removed, ...rest } = f;
                    return rest;
                  });
                }}
              />
              {fieldErrors.color ? (
                <span className="ne-field__error" role="alert">
                  {fieldErrors.color}
                </span>
              ) : null}
            </div>
            <Button
              type="submit"
              variant="secondary"
              disabled={createTag.isPending || newName.trim() === ''}
            >
              Add tag
            </Button>
          </form>

          {formError ? (
            <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
              {formError}
            </p>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button disabled={saving} onClick={() => void save()}>
              Save
            </Button>
            <Button variant="secondary" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
